import { z } from 'zod';

export const TaskStatusSchema = z.enum(['pending', 'in_progress', 'completed']);

export type TaskStatus = z.infer<typeof TaskStatusSchema>;

export const TaskRecordSchema = z
  .object({
    id: z.string().min(1),
    subject: z.string().min(1),
    description: z.string().min(1),
    activeForm: z.string().min(1).optional(),
    status: TaskStatusSchema,
    blocks: z.array(z.string().min(1)),
    blockedBy: z.array(z.string().min(1)),
    metadata: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

export type TaskRecord = z.infer<typeof TaskRecordSchema>;

export type CreateTaskInput = {
  subject: string;
  description: string;
  activeForm?: string;
  metadata?: Record<string, unknown>;
};

export type TaskListState = {
  list(): TaskRecord[];
  get(id: string): TaskRecord | undefined;
  create(input: CreateTaskInput): TaskRecord;
  save(task: TaskRecord): TaskRecord;
  delete(id: string): boolean;
};

export type TaskListStore = {
  transaction<T>(
    taskListId: string,
    fn: (state: TaskListState) => T | Promise<T>,
  ): Promise<T>;
};

export type TaskLifecycleHook = (
  task: TaskRecord,
) => void | string | Promise<void | string>;

export function cloneTask(task: TaskRecord): TaskRecord {
  return structuredClone(TaskRecordSchema.parse(task));
}

export function sortTasks(tasks: TaskRecord[]): TaskRecord[] {
  return [...tasks].sort(compareTaskIds);
}

function compareTaskIds(left: TaskRecord, right: TaskRecord): number {
  const leftNumber = Number(left.id);
  const rightNumber = Number(right.id);
  if (Number.isInteger(leftNumber) && Number.isInteger(rightNumber)) {
    return leftNumber - rightNumber;
  }
  return left.id.localeCompare(right.id);
}
